import {
    generateClient,
    GraphQLQuery,
} from 'aws-amplify/api';

import {
    getProviderConnection,
    listDnsProviders,
    listProviderConnections,
    previewProviderDisconnect,
} from './queries';

import {
    connectProvider,
    disconnectProvider,
    renameProviderConnection,
    revalidateProviderConnection,
    rotateProviderCredential,
    testProviderCredential,
} from './mutations';

import {
    ConnectProvider,
    ConnectProviderInput,
    DisconnectProvider,
    DisconnectProviderInput,
    DisconnectProviderResult,
    DnsProvider,
    GetProviderConnection,
    ListDnsProviders,
    ListProviderConnections,
    PreviewProviderDisconnect,
    ProviderConnection,
    ProviderConnectionsConnectionResult,
    ProviderCredentialTestResult,
    ProviderDisconnectPreview,
    RenameProviderConnection,
    RenameProviderConnectionInput,
    RevalidateProviderConnection,
    RevalidateProviderConnectionInput,
    RotateProviderCredential,
    RotateProviderCredentialInput,
    TestProviderCredential,
    TestProviderCredentialInput,
} from '../types';

const client = generateClient();

// FNP-1248 — the transport for the provider-connections module.
//
// ⚠️ ERRORS ARE RE-THROWN, NOT SWALLOWED INTO A NOTIFICATION HERE.
//
// The registrars module catches and calls showErrorNotifications; the zones
// module re-throws. This follows ZONES, and the reason is sharper here than
// anywhere else in the app: catching turns a failed call into `undefined`,
// which makes "the API rejected this" indistinguishable from "there is nothing
// here" to React Query — `isError` stays false and the table renders its empty
// state. For a module whose entire subject is WHY a credential does not work,
// erasing the failure at the transport would delete the product.
//
// The hooks own the notification; the components own the inline rendering.
//
// ⚠️ NOTHING IN THIS FILE LOGS ITS ARGUMENTS.
//
// The registrars service has a `console.debug('IN UPDATE REGISTRAR', id,
// record)` and the pattern is common in this repo. It MUST NOT be copied into
// `connect` or `rotate`: their input carries `credential.apiToken`, and a debug
// line would put a live bearer token into the browser console, into any
// console-capture the app has, and into a screenshot the moment a tenant
// reports a problem. There is deliberately no logging of any kind on the two
// credential-carrying functions — not even of "a connect was attempted".

// ─── READS ───

/**
 * One page of the calling tenant's connections.
 *
 * ⚠️ The tenant is a KEY CONDITION on the server side, not a filter — there is
 * no accountId parameter to pass and the API would not accept one.
 */
export const fetchRecords = async (
    limit: number,
    nextToken: string | null,
): Promise<ProviderConnectionsConnectionResult> => {

    const response = await client.graphql<GraphQLQuery<ListProviderConnections>>({
        query: listProviderConnections,
        variables: {
            limit,
            nextToken,
        },
    });

    return {
        items: response.data?.listProviderConnections?.items ?? [],
        // ⚠️ `?? null` and NOT `|| null`: coercing an empty-string token to null
        // would end pagination early. The token is the ONLY end-of-results
        // signal — `limit` applies BEFORE filtering on this platform, so a page
        // with zero items and a live token is normal.
        nextToken: response.data?.listProviderConnections?.nextToken ?? null,
    };
};

export const fetch = async (id: string): Promise<ProviderConnection | undefined> => {

    const response = await client.graphql<GraphQLQuery<GetProviderConnection>>({
        query: getProviderConnection,
        variables: {id},
    });

    return response.data?.getProviderConnection;
};

/**
 * The provider catalog, for the connect form's picker.
 *
 * Drains every page rather than returning one: the catalog is a small, bounded
 * set (ROUTE53, CLOUDFLARE, …) and a picker that silently omitted a provider
 * because it fell past the first page would be a defect with no visible cause.
 *
 * ⚠️ The loop is bounded. A server that returned the same token forever would
 * otherwise hang the form; `limit` applying before filtering means an empty
 * page with a live token is legitimate, so the guard is a page cap rather than
 * a check on `items.length`.
 */
export const fetchProviders = async (limit: number = 50): Promise<DnsProvider[]> => {

    const providers: DnsProvider[] = [];
    let nextToken: string | null = null;
    let pages = 0;
    const MAX_PAGES = 20;

    do {
        // ⚠️ The result is annotated explicitly. Without it TypeScript reports
        // TS7022 — `response` is referenced (via `nextToken`) in the loop
        // condition that feeds its own next iteration, which it cannot infer
        // through. The annotation breaks the cycle; it does not widen anything.
        const response: {
            data?: { listDnsProviders?: { items?: DnsProvider[], nextToken?: string | null } | null } | null
        } = await client.graphql<GraphQLQuery<ListDnsProviders>>({
            query: listDnsProviders,
            variables: {
                limit,
                nextToken,
            },
        });

        providers.push(...(response.data?.listDnsProviders?.items ?? []));
        nextToken = response.data?.listDnsProviders?.nextToken ?? null;
        pages += 1;
    } while (nextToken && pages < MAX_PAGES);

    return providers;
};

/**
 * What disconnecting this connection would destroy.
 *
 * ⚠️ MUST BE CALLED AND RENDERED BEFORE `disconnect`. The mutation does not ask
 * for confirmation because a server cannot verify a human saw anything — this
 * is how the interface does it. `useProviderDisconnect` will not arm the
 * confirm button until this has resolved.
 */
export const fetchDisconnectPreview = async (
    id: string,
): Promise<ProviderDisconnectPreview | undefined> => {

    const response = await client.graphql<GraphQLQuery<PreviewProviderDisconnect>>({
        query: previewProviderDisconnect,
        variables: {id},
    });

    return response.data?.previewProviderDisconnect;
};

// ─── WRITES ───

/**
 * Create a connection.
 *
 * ⚠️ A NON-ACTIVE RESULT IS A SUCCESSFUL CALL, NOT A REJECTED ONE.
 *
 * `connectProvider` validates through the adapter and then stores the
 * connection WHATEVER the adapter said. A first-time ASSUMED_ROLE connection
 * comes back INVALID or UNREACHABLE with a populated `secretRef.externalId`,
 * and that is the DESIGNED path — the external id is derived from the
 * connection id, so it could not have existed beforehand. This function
 * resolves normally in that case and the caller reads `status`.
 *
 * ⚠️ `input.credential` carries the tenant's secret. It is passed straight into
 * the mutation variables and never assigned anywhere else, never logged, and
 * never returned — the resolved value is a `ProviderConnection`, which has no
 * field capable of carrying it.
 */
export const connect = async (
    input: ConnectProviderInput,
): Promise<ProviderConnection | undefined> => {

    const response = await client.graphql<GraphQLQuery<ConnectProvider>>({
        query: connectProvider,
        variables: {input},
    });

    return response.data?.connectProvider;
};

/**
 * Check a credential and store nothing.
 *
 * ⚠️ FOR `ASSUMED_ROLE` THIS PROVES ONLY THAT THE ARN IS WELL-FORMED. The
 * external id minted here is a throwaway — never stored, never returned — so
 * the tenant cannot have put it in a trust policy and the assumption cannot
 * succeed. Callers must not present a failed Route 53 test as evidence the
 * tenant's role is wrong. See hooks/useTestProviderCredential.
 */
export const testCredential = async (
    input: TestProviderCredentialInput,
): Promise<ProviderCredentialTestResult | undefined> => {

    const response = await client.graphql<GraphQLQuery<TestProviderCredential>>({
        query: testProviderCredential,
        variables: {input},
    });

    return response.data?.testProviderCredential;
};

/**
 * Replace the credential on an existing connection — the RE-VALIDATE path.
 *
 * This is how a tenant fixes a connection after adding Flexi's external id to
 * their role's trust policy: the same roleArn is submitted again, and because
 * the connection KEEPS its external id across a rotation, the value they pasted
 * is still the right one.
 *
 * A rotation to a credential the provider rejects records the failure and KEEPS
 * the connection. It is never deleted for being broken.
 */
export const rotateCredential = async (
    input: RotateProviderCredentialInput,
): Promise<ProviderConnection | undefined> => {

    const response = await client.graphql<GraphQLQuery<RotateProviderCredential>>({
        query: rotateProviderCredential,
        variables: {input},
    });

    return response.data?.rotateProviderCredential;
};

/**
 * FNP-1269 — re-check the credential that is ALREADY stored.
 *
 * ⚠️ THE INPUT IS AN ID. THERE IS NOTHING ELSE TO SEND.
 *
 * This is the path a tenant takes after adding Flexi's external id to their
 * role's trust policy: the credential has not changed, so asking them to
 * re-supply it is asking them to retype an ARN they can already see — where a
 * typo would silently repoint the connection at a different role. `rotate`
 * remains the right call when the credential genuinely changed, which for an
 * API_TOKEN connection is the only reason to be here at all.
 *
 * ⚠️ IT SENDS NO `writable` AND NO CERTIFICATE CAPABILITY. The omission is what
 * PRESERVES the connection's existing declarations — see
 * `RevalidateProviderConnectionInput`. Do not add them here "for completeness".
 *
 * Same status contract as rotate: a re-validation that lands INVALID is a
 * SUCCESSFUL call that recorded a failure. The connection is kept, and the
 * caller reads `status`.
 */
export const revalidate = async (
    input: RevalidateProviderConnectionInput,
): Promise<ProviderConnection | undefined> => {

    const response = await client.graphql<GraphQLQuery<RevalidateProviderConnection>>({
        query: revalidateProviderConnection,
        variables: {input},
    });

    return response.data?.revalidateProviderConnection;
};

/**
 * Delete the secret, then the record.
 *
 * ⚠️ Named `disconnect`, NOT `deleteRecord`. The pattern doc's generic trio
 * assumes an entity whose delete is a row removal; this one revokes a tenant's
 * integration and destroys a secret, and the preview exists because the cost is
 * not obvious from the row. Naming it `deleteRecord` would invite a caller to
 * wire it to a bare delete button — the one thing this module must not have.
 */
export const disconnect = async (
    input: DisconnectProviderInput,
): Promise<DisconnectProviderResult | undefined> => {

    const response = await client.graphql<GraphQLQuery<DisconnectProvider>>({
        query: disconnectProvider,
        variables: {input},
    });

    return response.data?.disconnectProvider;
};

/**
 * FNP-1304 — change the tenant's label on a connection.
 *
 * ⚠️ NOT `updateRecord`. The pattern doc's generic trio assumes an entity whose
 * update writes whatever the form holds; almost every field on a connection is
 * a record of what the PROVIDER said, and is not the caller's to set. This
 * writes one field, and is named for the one thing it does so nobody wires it
 * to a general edit form.
 *
 * It does not re-validate. Renaming an INVALID connection leaves it INVALID.
 */
export const rename = async (
    input: RenameProviderConnectionInput,
): Promise<ProviderConnection | undefined> => {

    const response = await client.graphql<GraphQLQuery<RenameProviderConnection>>({
        query: renameProviderConnection,
        variables: {input},
    });

    return response.data?.renameProviderConnection;
};
